// NerdPhys core — pure physics, no UI. Covered by tests/phystest.mjs
// against known textbook values. SI units throughout. g = 9.81 m/s².

export const G = 9.81;

// Shared finite-input guard. Non-finite values (NaN/Infinity) must never
// leak through as ok:true — that produces a silently wrong displayed answer.
function finite(...xs) {
  return xs.every(Number.isFinite);
}

// ---------- Projectile motion (signature tool) ----------
// Launch from ground (h0 default 0). Angle in degrees, v0 in m/s.
// No air resistance (standard intro-physics model — stated honestly).
// Intro-physics scope: launch angle restricted to 0–90° (a backward
// launch is almost always user error and the plot would mislead).
export function projectile(v0, angleDeg, h0 = 0, g = G) {
  if (!finite(v0, angleDeg, h0, g)) return { ok: false, error: "Enter finite numeric values" };
  if (v0 < 0 || g <= 0) return { ok: false, error: "v0 must be ≥ 0 and g > 0" };
  if (angleDeg < 0 || angleDeg > 90) return { ok: false, error: "Launch angle must be between 0° and 90°" };
  const a = (angleDeg * Math.PI) / 180;
  const vx = v0 * Math.cos(a);
  const vy = v0 * Math.sin(a);
  // Time of flight: solve h0 + vy t - ½ g t² = 0 for the positive root.
  const disc = vy * vy + 2 * g * h0;
  if (disc < 0) return { ok: false, error: "Projectile never reaches the ground with these values" };
  const tFlight = (vy + Math.sqrt(disc)) / g;
  const range = vx * tFlight;
  // Apex: time when vertical velocity = 0 (only if it rises).
  const tApex = vy > 0 ? vy / g : 0;
  const maxHeight = h0 + (vy > 0 ? (vy * vy) / (2 * g) : 0);
  return {
    ok: true,
    vx, vy,
    timeOfFlight: tFlight,
    range,
    maxHeight,
    timeToApex: tApex,
    impactSpeed: Math.sqrt(vx * vx + (vy * vy + 2 * g * h0)),
  };
}

// Position/velocity sample at time t (for the trajectory plot).
export function projectileAt(v0, angleDeg, t, h0 = 0, g = G) {
  const a = (angleDeg * Math.PI) / 180;
  const vx = v0 * Math.cos(a);
  const vy = v0 * Math.sin(a);
  return {
    x: vx * t,
    y: h0 + vy * t - 0.5 * g * t * t,
    vx,
    vy: vy - g * t,
  };
}

// ---------- Kinematics (SUVAT) ----------
// Solve constant-acceleration motion given any 3 of {u, v, a, t, s}.
// Returns the two unknowns (or one) with the equations used.
export function suvat(input) {
  const { u, v, a, t, s } = input;
  const known = { u, v, a, t, s };
  const have = Object.keys(known).filter((k) => known[k] != null && !isNaN(known[k]));
  if (have.length < 3) return { ok: false, error: "Provide at least 3 of: u, v, a, t, s" };
  if (!Object.values(known).every((x) => x == null || Number.isFinite(x)))
    return { ok: false, error: "Enter finite numeric values" };

  // Solve from a chosen root preference for the one square-root equation
  // that can yield two physical branches (v²=u²+2as / u²=v²−2as).
  // sign = +1 (principal) or -1 (the other physical branch).
  function solveBranch(sign) {
    let { u: U, v: V, a: A, t: T, s: S } = known;
    const steps = [];
    let usedSqrtBranch = false;
    for (let pass = 0; pass < 4; pass++) {
      if (V == null && U != null && A != null && T != null) { V = U + A * T; steps.push("v = u + at"); }
      if (S == null && U != null && T != null && A != null) { S = U * T + 0.5 * A * T * T; steps.push("s = ut + ½at²"); }
      if (A == null && V != null && U != null && T != null && T !== 0) { A = (V - U) / T; steps.push("a = (v − u)/t"); }
      if (T == null && V != null && U != null && A != null && A !== 0) { T = (V - U) / A; steps.push("t = (v − u)/a"); }
      if (V == null && U != null && A != null && S != null) {
        const d = U * U + 2 * A * S;
        if (d >= 0) { V = sign * Math.sqrt(d); usedSqrtBranch = d > 1e-12; steps.push("v² = u² + 2as"); }
      }
      if (U == null && V != null && A != null && S != null) {
        const d = V * V - 2 * A * S;
        if (d >= 0) { U = sign * Math.sqrt(d); usedSqrtBranch = d > 1e-12; steps.push("u = √(v² − 2as)"); }
      }
      if (S == null && U != null && V != null && T != null) { S = ((U + V) / 2) * T; steps.push("s = ½(u + v)t"); }
      if (T == null && S != null && U != null && V != null && U + V !== 0) { T = (2 * S) / (U + V); steps.push("t = 2s/(u + v)"); }
    }
    const out = { u: U, v: V, a: A, t: T, s: S };
    return { out, steps: [...new Set(steps)], usedSqrtBranch };
  }

  const unknown = ["u", "v", "a", "t", "s"].filter((k) => known[k] == null || isNaN(known[k]));
  const principal = solveBranch(+1);
  const solved = unknown.filter((k) => principal.out[k] != null && !isNaN(principal.out[k]));
  if (solved.length === 0) return { ok: false, error: "Could not solve with the given values" };

  const base = {
    ok: true,
    result: principal.out,          // backward-compatible singular result
    solvedFor: solved,
    steps: principal.steps,
  };

  // Two physical branches exist only when a non-degenerate square-root
  // equation was used (object passes the same s with ±velocity / a
  // motion has two valid times). Expose both, additively.
  if (principal.usedSqrtBranch) {
    const other = solveBranch(-1);
    const differs = ["u", "v", "a", "t", "s"].some(
      (k) => Math.abs((principal.out[k] ?? 0) - (other.out[k] ?? 0)) > 1e-9
    );
    if (differs && solved.every((k) => other.out[k] != null && !isNaN(other.out[k]))) {
      base.ambiguous = true;
      base.solutions = [principal.out, other.out];
      base.warnings = [
        "This input has two valid physical solutions (e.g. the object passes this point on the way up and on the way down). Both are shown.",
      ];
    }
  }
  return base;
}

// ---------- Newton's second law ----------
// F = m a. Solve for the missing one of {F, m, a}.
export function newton({ F, m, a }) {
  if (!Object.values({ F, m, a }).every((x) => x == null || Number.isFinite(x)))
    return { ok: false, error: "Enter finite numeric values" };
  const n = [F, m, a].filter((x) => x != null && !isNaN(x)).length;
  if (n < 2) return { ok: false, error: "Provide two of: F, m, a" };
  if (m != null && m < 0) return { ok: false, error: "Mass must be ≥ 0" };
  if (F == null) return { ok: true, result: { F: m * a, m, a }, solvedFor: "F", formula: "F = ma" };
  if (m == null) {
    if (a === 0) return { ok: false, error: "a = 0 → mass indeterminate" };
    return { ok: true, result: { F, m: F / a, a }, solvedFor: "m", formula: "m = F/a" };
  }
  if (m === 0) return { ok: false, error: "m = 0 → acceleration undefined" };
  return { ok: true, result: { F, m, a: F / m }, solvedFor: "a", formula: "a = F/m" };
}

// ---------- Energy & work ----------
export function kineticEnergy(m, v) {
  if (!finite(m, v)) return { ok: false, error: "Enter finite numeric values" };
  if (m < 0) return { ok: false, error: "Mass must be ≥ 0" };
  return { ok: true, result: 0.5 * m * v * v, formula: "KE = ½mv²" };
}
export function potentialEnergy(m, h, g = G) {
  if (!finite(m, h, g)) return { ok: false, error: "Enter finite numeric values" };
  if (m < 0) return { ok: false, error: "Mass must be ≥ 0" };
  if (g <= 0) return { ok: false, error: "g must be > 0" };
  return { ok: true, result: m * g * h, formula: "PE = mgh" };
}
export function work(force, distance, angleDeg = 0) {
  if (!finite(force, distance, angleDeg)) return { ok: false, error: "Enter finite numeric values" };
  const w = force * distance * Math.cos((angleDeg * Math.PI) / 180);
  return { ok: true, result: w, formula: "W = Fd·cosθ" };
}

// ---------- Ohm's law ----------
// V = I R, plus power P = V I. Solve missing of {V, I, R}.
export function ohms({ V, I, R }) {
  if (!Object.values({ V, I, R }).every((x) => x == null || Number.isFinite(x)))
    return { ok: false, error: "Enter finite numeric values" };
  const n = [V, I, R].filter((x) => x != null && !isNaN(x)).length;
  if (n < 2) return { ok: false, error: "Provide two of: V, I, R" };
  if (R != null && R < 0) return { ok: false, error: "Resistance must be ≥ 0" };
  let rV = V, rI = I, rR = R;
  if (V == null) rV = I * R;
  else if (I == null) { if (R === 0) return { ok: false, error: "R = 0 → current undefined" }; rI = V / R; }
  else if (R == null) { if (I === 0) return { ok: false, error: "I = 0 → resistance undefined" }; rR = V / I; }
  return { ok: true, result: { V: rV, I: rI, R: rR, P: rV * rI }, formula: "V = IR · P = VI" };
}

export function round(x, p = 6) {
  if (typeof x !== "number" || !isFinite(x)) return x;
  const f = Math.pow(10, p);
  return Math.round(x * f) / f;
}


// ---------- Wave relationship: v = f λ ----------
// Provide exactly two of {v, f, lambda}; solves the third.
// period T = 1/f also returned. SI: v m/s, f Hz, λ m.
export function waves({ v = null, f = null, lambda = null }) {
  const given = [v, f, lambda].filter((x) => x !== null).length;
  if (given !== 2) return { ok: false, error: "Provide exactly two of speed, frequency, wavelength" };
  if (!finite(...[v, f, lambda].filter((x) => x !== null)))
    return { ok: false, error: "Enter finite numeric values" };
  const pos = [v, f, lambda].filter((x) => x !== null).every((x) => x > 0);
  if (!pos) return { ok: false, error: "Speed, frequency and wavelength must be positive" };
  let V = v, F = f, L = lambda;
  if (v === null) V = f * lambda;
  else if (f === null) F = v / lambda;
  else L = v / f;
  const T = 1 / F;
  if (!finite(V, F, L, T) || V <= 0 || F <= 0 || L <= 0 || T <= 0)
    return { ok: false, error: "Values are too large or too small to compute safely" };
  // Worked steps reflect the ACTUAL branch taken (which unknown was
  // derived), then T = 1/f. round() keeps displayed math == result.
  const r = (x) => Math.round(x * 1e6) / 1e6;
  const steps = [];
  if (v === null) steps.push(`v = f·λ = ${f} × ${lambda} = ${r(V)} m/s`);
  else if (f === null) steps.push(`f = v/λ = ${v} ÷ ${lambda} = ${r(F)} Hz`);
  else steps.push(`λ = v/f = ${v} ÷ ${f} = ${r(L)} m`);
  steps.push(`T = 1/f = 1 ÷ ${r(F)} = ${r(T)} s`);
  return { ok: true, result: { v: V, f: F, lambda: L, T }, formula: "v = f·λ   ·   T = 1/f", steps };
}

// ---------- 1D collision (momentum conservation) ----------
// Two bodies, head-on. e = restitution: 1 perfectly elastic, 0 perfectly
// inelastic (stick). Returns final velocities + KE before/after + loss.
export function collision1D(m1, u1, m2, u2, e = 1) {
  if (!finite(m1, u1, m2, u2, e)) return { ok: false, error: "Enter finite numeric values" };
  if (m1 <= 0 || m2 <= 0) return { ok: false, error: "Masses must be positive" };
  if (e < 0 || e > 1) return { ok: false, error: "Restitution e must be between 0 and 1" };
  // Conservation of momentum + restitution:
  // v2 - v1 = -e (u2 - u1)
  const totalM = m1 + m2;
  const v1 = (m1 * u1 + m2 * u2 - m2 * e * (u1 - u2)) / totalM;
  const v2 = (m1 * u1 + m2 * u2 + m1 * e * (u1 - u2)) / totalM;
  const keBefore = 0.5 * m1 * u1 * u1 + 0.5 * m2 * u2 * u2;
  // Stable closed form — avoids catastrophic cancellation of two huge
  // near-equal KE totals (was producing negative "loss"). KE lost in a
  // 1D restitution collision = ½ μ (1−e²) (Δu)², μ = reduced mass.
  const mu = (m1 * m2) / totalM;
  const du = u1 - u2;
  const keLoss = 0.5 * mu * (1 - e * e) * du * du;
  const keAfter = keBefore - keLoss;
  const pBefore = m1 * u1 + m2 * u2;
  const pAfter = m1 * v1 + m2 * v2;
  if (!finite(v1, v2, pBefore, pAfter, keBefore, keAfter, keLoss))
    return { ok: false, error: "Values are too large to compute safely" };
  return { ok: true, result: { v1, v2, pBefore, pAfter, keBefore, keAfter, keLoss } };
}
